import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

/**
 * Session storage key for the active patient context.
 * Blank string means "no patient selected" (user-only context).
 */
const SESSION_KEY = "pdx.activeId.v1";

const PatientContext = createContext(null);

/**
 * @typedef {object} PatientContextValue
 * @property {string} activeId
 *   Currently selected patient or participant identifier for this browser tab.
 * @property {Function} setActiveId
 *   Updates the active patient ID and mirrors it into session storage.
 */

/**
 * Normalise a patient id for consistent comparisons/paths.
 *
 * @param {unknown} v
 * @returns {string} trimmed patient id or "" if empty/invalid
 */
function normalizeId(v) {
  if (v === undefined || v === null) return "";
  const s = String(v).trim();
  return s.length ? s : "";
}

/**
 * Provider that stores the "active patient id" for the currently signed-in user.
 * This is session-scoped (sessionStorage) by default, matching your prefetch cache.
 *
 * @param {object} props
 * @param {*} props.children
 * @returns {JSX.Element}
 */
export function PatientProvider({ children }) {
  const [activeId, setActiveIdState] = useState(() => {
    try {
      return normalizeId(sessionStorage.getItem(SESSION_KEY));
    } catch {
      return "";
    }
  });

  /**
   * Sets the active patient id (session-scoped).
   * Pass "" to clear the patient selection (user-only context).
   *
   * @param {string} next
   */
  const setActiveId = useCallback((next) => {
    const norm = normalizeId(next);
    setActiveIdState(norm);

    try {
      if (!norm) sessionStorage.removeItem(SESSION_KEY);
      else sessionStorage.setItem(SESSION_KEY, norm);
    } catch {
      // ignore storage errors
    }
  }, []);

  const value = useMemo(
    () => ({
      activeId,
      setActiveId,
    }),
    [activeId, setActiveId],
  );

  return (
    <PatientContext.Provider value={value}>{children}</PatientContext.Provider>
  );
}

/**
 * Hook to consume the patient context.
 *
 * @returns {PatientContextValue}
 */
export function usePatient() {
  const ctx = useContext(PatientContext);
  if (!ctx) throw new Error("usePatient must be used within a PatientProvider");
  return ctx;
}
